/**
 * Neighbourhood Vibe — 3-4 sentence AI summary derived from OSM POI
 * counts (via `/api/neighbourhood/vibe`).
 *
 * Rendered above the amenity chips on the property-detail page.
 * Fails silently if the endpoint returns non-200 (Overpass or Claude
 * unavailable) — better to hide than to show a stale/empty pill.
 */
import React, { useEffect, useState } from 'react';
import { Sparkles, Loader2 } from 'lucide-react';

const _vibeCache = new Map();

export const NeighbourhoodVibe = ({ lat, lng }) => {
  const [vibe, setVibe] = useState(null);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (lat == null || lng == null) return;
    const key = `${Number(lat).toFixed(3)},${Number(lng).toFixed(3)}`;
    if (_vibeCache.has(key)) { setVibe(_vibeCache.get(key)); return; }

    let cancelled = false;
    setLoading(true);
    setFailed(false);

    // Retry once on transient failure — Overpass and the LLM are both
    // occasionally slow / rate-limited and a quick second attempt
    // usually succeeds.
    const fetchOnce = () => fetch(
      `${process.env.REACT_APP_BACKEND_URL}/api/neighbourhood/vibe?lat=${lat}&lng=${lng}`,
    ).then((r) => r.ok ? r.json() : null).catch(() => null);

    (async () => {
      let data = await fetchOnce();
      if (!data?.vibe) {
        await new Promise((r) => setTimeout(r, 1500));
        data = await fetchOnce();
      }
      if (cancelled) return;
      if (data?.vibe) {
        _vibeCache.set(key, data.vibe);
        setVibe(data.vibe);
      } else {
        setFailed(true);
      }
      setLoading(false);
    })();

    return () => { cancelled = true; };
  }, [lat, lng]);

  // Keep the card mounted on failure so it doesn't flash-and-vanish —
  // that felt broken to buyers. Show a calm "unavailable" state
  // instead so at least the affordance is discoverable next time.
  if (!loading && !vibe && !failed) return null;

  return (
    <div
      className="rounded-lg bg-gradient-to-r from-[#f5efff] to-[#eef7ff] ring-1 ring-[#7042cc]/20 px-3.5 py-2.5 mb-3"
      data-testid="neighbourhood-vibe"
    >
      <div className="flex items-start gap-2">
        <Sparkles className="h-4 w-4 text-[#7042cc] mt-0.5 shrink-0" />
        <div className="flex-1">
          <p className="text-[10px] font-bold uppercase tracking-wider text-[#5b35a8] mb-0.5">
            What this neighbourhood is really like
          </p>
          {loading && !vibe && (
            <div className="flex items-center gap-1.5 text-[12px] text-gray-500">
              <Loader2 className="h-3 w-3 animate-spin" /> Analysing local amenities…
            </div>
          )}
          {vibe && (
            <p className="text-[13px] text-gray-800 leading-relaxed">{vibe}</p>
          )}
          {!loading && !vibe && failed && (
            <p className="text-[12px] text-gray-500 italic"
                data-testid="neighbourhood-vibe-unavailable">
              We couldn&apos;t analyse this neighbourhood just now — check back in a few minutes.
            </p>
          )}
          {vibe && (
            <p className="text-[9px] text-gray-400 mt-1 italic">
              AI-generated from OpenStreetMap data — not a substitute for a visit.
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default NeighbourhoodVibe;
